import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import SpinnerModal from '../../ShareComponent/Spinner/SpinnerModal';
import CommonModal from '../Common/CommonModal';
import { AdminBannersAPI, API_BASE_ORIGIN } from '../../Services/ECommerceAPI';
import '../Common/common.css';

const BannerManagement = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // create, edit
  const [selected, setSelected] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    imageUrl: '',
    linkUrl: '',
    isActive: true,
    sortOrder: 0,
  });

  const previewUrl = useMemo(() => {
    const raw = formData.imageUrl?.trim();
    if (!raw) return '';
    if (raw.startsWith('http')) return raw;
    if (raw.startsWith('/')) return `${API_BASE_ORIGIN}${raw}`;
    return raw;
  }, [formData.imageUrl]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await AdminBannersAPI.getAll();
      setBanners(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Failed to load banners');
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openCreate = () => {
    setModalMode('create');
    setSelected(null);
    setSelectedFile(null);
    setFormData({
      title: '',
      subtitle: '',
      imageUrl: '',
      linkUrl: '',
      isActive: true,
      sortOrder: banners.length,
    });
    setShowModal(true);
  };

  const openEdit = (b) => {
    setModalMode('edit');
    setSelected(b);
    setSelectedFile(null);
    setFormData({
      title: b.title ?? b.Title ?? '',
      subtitle: b.subtitle ?? b.Subtitle ?? '',
      imageUrl: b.imageUrl ?? b.ImageUrl ?? '',
      linkUrl: b.linkUrl ?? b.LinkUrl ?? '',
      isActive: (b.isActive ?? b.IsActive) ?? true,
      sortOrder: Number(b.sortOrder ?? b.SortOrder ?? 0),
    });
    setShowModal(true);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    try {
      setIsLoading(true);
      const res = await AdminBannersAPI.uploadImage(selectedFile);
      const url = res?.url || res?.Url || '';
      if (!url) throw new Error('No url returned');
      setFormData((p) => ({ ...p, imageUrl: url.startsWith('/') ? url : '/' + url }));
      toast.success('Image uploaded');
    } catch (e) {
      toast.error('Failed to upload image');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title?.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.imageUrl?.trim()) {
      toast.error('Image is required');
      return;
    }
    try {
      setIsLoading(true);
      const payload = {
        title: formData.title.trim(),
        subtitle: formData.subtitle?.trim() || null,
        imageUrl: formData.imageUrl.trim(),
        linkUrl: formData.linkUrl?.trim() || null,
        isActive: !!formData.isActive,
        sortOrder: Number(formData.sortOrder) || 0,
      };
      if (modalMode === 'create') {
        await AdminBannersAPI.create(payload);
        toast.success('Banner created');
      } else {
        const id = selected?.bannerID ?? selected?.BannerID;
        await AdminBannersAPI.update(id, payload);
        toast.success('Banner updated');
      }
      setShowModal(false);
      await loadData();
    } catch (e) {
      toast.error('Failed to save banner');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (b) => {
    const id = b.bannerID ?? b.BannerID;
    if (!window.confirm('Delete this banner?')) return;
    try {
      setIsLoading(true);
      await AdminBannersAPI.remove(id);
      toast.success('Banner deleted');
      await loadData();
    } catch (e) {
      toast.error('Failed to delete banner');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="page-container">
      <SpinnerModal show={isLoading} />
      <div className="pharmacy-header">
        <h1>Banner Management</h1>
        <p>Manage homepage banners (image, title, link, active status, sort order).</p>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
        <button className="common-button primary" type="button" onClick={openCreate}>
          + Add Banner
        </button>
        <button className="common-button" type="button" onClick={loadData}>
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading banners...</div>
      ) : banners.length === 0 ? (
        <div className="empty-state">
          <h3>No banners yet</h3>
          <p>Add a banner to show it on the public homepage.</p>
        </div>
      ) : (
        <div className="card">
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Preview</th>
                  <th>Title</th>
                  <th>Active</th>
                  <th>Sort</th>
                  <th>Link</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {banners.map((b) => {
                  const imgRaw = b.imageUrl ?? b.ImageUrl ?? '';
                  const img = imgRaw && imgRaw.startsWith('/') ? `${API_BASE_ORIGIN}${imgRaw}` : imgRaw;
                  const id = b.bannerID ?? b.BannerID;
                  return (
                    <tr key={id}>
                      <td style={{ width: 160 }}>
                        <div style={{ width: 140, height: 70, borderRadius: 10, overflow: 'hidden', background: '#eef0f4' }}>
                          {img ? <img src={img} alt={b.title ?? b.Title ?? 'banner'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 700 }}>{b.title ?? b.Title}</div>
                        <div style={{ color: 'var(--text-secondary)' }}>{b.subtitle ?? b.Subtitle}</div>
                      </td>
                      <td>{(b.isActive ?? b.IsActive) ? 'Yes' : 'No'}</td>
                      <td>{b.sortOrder ?? b.SortOrder}</td>
                      <td style={{ maxWidth: 260, wordBreak: 'break-word' }}>{(b.linkUrl ?? b.LinkUrl) || '—'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button className="common-button" type="button" onClick={() => openEdit(b)}>Edit</button>
                          <button className="common-button danger" type="button" onClick={() => handleDelete(b)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <CommonModal
        show={showModal}
        onHide={() => setShowModal(false)}
        title={modalMode === 'create' ? 'Add Banner' : 'Edit Banner'}
        size="lg"
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 16 }}>
          <div>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Simple is More"
              />
            </div>
            <div className="form-group">
              <label>Subtitle</label>
              <input
                type="text"
                value={formData.subtitle}
                onChange={(e) => setFormData((p) => ({ ...p, subtitle: e.target.value }))}
                placeholder="e.g. Limited editions • styles"
              />
            </div>
            <div className="form-group">
              <label>Link URL</label>
              <input
                type="text"
                value={formData.linkUrl}
                onChange={(e) => setFormData((p) => ({ ...p, linkUrl: e.target.value }))}
                placeholder="/shop or /product/slug"
              />
            </div>
            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label>Sort order</label>
                <input
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData((p) => ({ ...p, sortOrder: e.target.value }))}
                />
              </div>
              <div className="form-group checkbox-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 26 }}>
                  <input
                    type="checkbox"
                    checked={!!formData.isActive}
                    onChange={(e) => setFormData((p) => ({ ...p, isActive: e.target.checked }))}
                  />
                  Active
                </label>
              </div>
            </div>

            <div className="form-group">
              <label>Banner Image *</label>
              <input type="file" accept="image/*" onChange={(e) => setSelectedFile(e.target.files?.[0] || null)} />
              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button type="button" className="common-button" onClick={handleUpload} disabled={!selectedFile || isLoading}>
                  Upload image
                </button>
                <input
                  type="text"
                  value={formData.imageUrl}
                  onChange={(e) => setFormData((p) => ({ ...p, imageUrl: e.target.value }))}
                  placeholder="Or paste image URL (prefer /uploads/banners/..)"
                  style={{ flex: 1 }}
                />
              </div>
            </div>
          </div>

          <div>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Preview</div>
            <div style={{ width: '100%', height: 220, borderRadius: 14, background: '#eef0f4', overflow: 'hidden' }}>
              {previewUrl ? <img src={previewUrl} alt="banner preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 14, justifyContent: 'flex-end' }}>
              <button type="button" className="common-button" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="button" className="common-button primary" onClick={handleSave} disabled={isLoading}>
                Save
              </button>
            </div>
          </div>
        </div>
      </CommonModal>
    </div>
  );
};

export default BannerManagement;

